import React, { useMemo, useRef } from 'react';
import { getDvaApp } from 'umi';
import SteamAllReviews from '@op/pages/SteamPerformance/SteamRatings/SteamAllReviews';
import useGetPcConsoleStats from '@op/pages/gamestore/hooks/useGetPcConsoleStats';
import useGetPcConsoleTimeline from '@op/pages/gamestore/hooks/useGetPcConsoleTimeline';
import { IStudioSteamAllReviewsWrapProps } from './type';

const OPINION_COMMON_NAMESPACE = 'opinionCommon';

/** opinion API 请求所需的默认筛选参数（空值） */
const DEFAULT_OPINION_FILTER = {
  language: [],
  sentiment: [],
  topic: [],
  topic_rule: 'include',
  reviews_rating: [],
  steam_ids: [],
  starred_tag: 0,
  refunded: 0,
  free_played: 0,
  steam_purchase: 0,
  funnys: [],
  helpfuls: [],
  record_times: [],
  review_times: [],
  early_access: 0,
  game_product_ids: [],
  is_valid: [],
  keywords_list: [],
  keywords_list_mode: 'include',
};

/**
 * 动态注册 opinionCommon model（最小版本）
 * opinion 模块的 SteamAllReviews 内部 ReviewItem 通过 connect 访问
 * state.opinionCommon.wholeGameInfo，若 model 未注册会导致崩溃
 */
function ensureOpinionCommonModel(): void {
  const app = getDvaApp();
  if (!app) return;

  const hasModel = app._models?.some(
    (m: any) => m.namespace === OPINION_COMMON_NAMESPACE,
  );
  if (hasModel) return;

  app.model({
    namespace: OPINION_COMMON_NAMESPACE,
    state: {
      gameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      showGameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      listMenu: [],
      listMenuLoading: true,
      excludeSource: [],
      hidePageIds: [],
      wholeGameInfo: { edition_unified_id: '', entity_type: '', id_type: '' },
      changeGameVisible: false,
      pageLoading: false,
      gameInfoLoading: true,
    },
    reducers: {
      save(state: any, { payload }: any) {
        return { ...state, ...payload };
      },
    },
  });
}

const STEAM_CHANNEL: Array<'steam'> = ['steam'];

const StudioSteamAllReviewsWrap: React.FC<IStudioSteamAllReviewsWrapProps> = (props) => {
  const { data, gameCode, filterVal: propsFilterVal, style, className } = props;
  const modelRegistered = useRef(false);

  if (!modelRegistered.current) {
    ensureOpinionCommonModel();
    modelRegistered.current = true;
  }

  const gameInfo = useMemo(() => {
    const gameList = data?.studioData?.game_list || [];
    const game = gameList.find((g) => g.game_code === gameCode);
    if (!game) return null;
    return {
      edition_unified_id: game.unified_edition_id,
      id_type: game.id_type,
      entity_type: game.game_type,
    };
  }, [data?.studioData?.game_list, gameCode]);

  const filterVal = propsFilterVal || data?.filterVal || {};

  const filter = useMemo(() => {
    if (!gameInfo) return null;
    const startTime = filterVal.start_time;
    const endTime = filterVal.end_time;
    const dateType = filterVal.date_type;
    if (!startTime || !endTime) return null;
    return {
      ...gameInfo,
      start_time: `${startTime} 00:00:00`,
      end_time: `${endTime} 23:59:59`,
      date_type: dateType || 'daily',
      channel: ['steam'],
      channel_type: 'comments',
      steam_filter_type: 'steam',
      ...DEFAULT_OPINION_FILTER,
    };
  }, [gameInfo, filterVal]);

  const { userScore, userScoreLoading } = useGetPcConsoleStats({
    keepParams: filter,
    channel: STEAM_CHANNEL,
    needLink: false,
  });

  const {
    globalTrendsTimeline,
    userScoreTrendsLoading,
    userScoreTrendsApiError,
  } = useGetPcConsoleTimeline({
    keepParams: filter,
    channel: STEAM_CHANNEL,
  });

  if (!filter) return null;

  return (
    <div style={style} className={className}>
      <SteamAllReviews
        userScore={userScore}
        filter={filter}
        loading={userScoreLoading}
        timeline={globalTrendsTimeline}
        timelineLoading={userScoreTrendsLoading}
        apiErr={userScoreTrendsApiError}
      />
    </div>
  );
};

export default StudioSteamAllReviewsWrap;
